/**
 * The typed-amount box with its commas, and where the caret goes. Writing the formatted text into a field sends the caret to the
 * end, so editing in the middle of a number jumped it, and Backspace right after a comma only removed the comma (which the
 * formatting put back), so no digit went.
 */
export interface TypedAmount {
  /** The digits with their commas ('' for none). */
  text: string;
  /** Where the caret goes in text. */
  caret: number;
}

/**
 * @param raw        what the field holds now, after the browser's edit
 * @param caret      the caret in raw after that edit
 * @param prevDigits the digits the field held before the edit
 * @param inputType  the input event's inputType ('deleteContentBackward' for Backspace, 'deleteContentForward' for Delete)
 */
export function formatTypedAmount(raw: string, caret: number, prevDigits: string, inputType?: string): TypedAmount {
  const at = Math.max(0, Math.min(caret, raw.length));
  let digits = raw.replace(/\D/g, '');
  // Digits to the left of the caret: the caret's place is kept by counting them, whatever the commas do.
  let left = raw.slice(0, at).replace(/\D/g, '').length;
  // Only a comma went (same digits as before): the visitor meant the digit beside it.
  if (digits === prevDigits && digits !== '') {
    if (inputType === 'deleteContentBackward' && left > 0) {
      digits = digits.slice(0, left - 1) + digits.slice(left);
      left -= 1;
    } else if (inputType === 'deleteContentForward' && left < digits.length) {
      digits = digits.slice(0, left) + digits.slice(left + 1);
    }
  }
  if (digits.length > 9) {
    digits = digits.slice(0, 9);
    left = Math.min(left, 9);
  }
  const text = digits ? Number(digits).toLocaleString('en-US') : '';
  // After the left-th digit of the formatted text (before any comma that follows it).
  let seen = 0;
  let pos = 0;
  if (left > 0) {
    for (pos = 0; pos < text.length; pos++) {
      if (/\d/.test(text[pos]) && ++seen === left) { pos++; break; }
    }
  }
  return { text, caret: pos };
}
